import classnames from 'classnames'
import { createSearchParams, useLocation, useNavigate } from 'react-router'

import { Button, DoughnutChart } from '@/components'
import { SystemStatusSubcategoryEnum } from '@/enums'
import { useAnalytics, useFormData, useQuery } from '@/hooks'
import { SystemStatusCategory } from '@/models'
import { MonitoringSummaryDataType, FormChangeEventHandler } from '@/types'

type MonitoringIssuesChartProps = {
    summary: MonitoringSummaryDataType
    type?: 'donut' | 'half-donut'
    className?: string
    label?: string
    secondaryDesign?: boolean

    onChange?: FormChangeEventHandler
}

const MonitoringIssuesChart = ({
    summary,
    className = '',
    type = 'donut',
    label = 'Connected Sites',
    secondaryDesign = false,
    onChange,
    ...props
}: MonitoringIssuesChartProps) => {
    const { trackEvent } = useAnalytics()
    const { convert } = useFormData()
    const location = useLocation()
    const query = useQuery()
    const navigate = useNavigate()

    const getMonitoringIndexPage = (filters: { statuses: SystemStatusSubcategoryEnum[] }) => {
        const params = convert(location.pathname === '/monitoring' ? { ...query, ...filters } : filters, {}) as {
            [key: string]: string
        }
        return `/monitoring?${createSearchParams(params)}`
    }

    const subcategories = secondaryDesign
        ? SystemStatusCategory.connectedSitesCategory.secondarySortedSubcategories
        : SystemStatusCategory.connectedSitesCategory.subcategories

    const trackClickEvent = (index: number) => {
        if (typeof onChange !== 'undefined') {
            onChange({
                target: {
                    name: 'statuses',
                    value: [subcategories[index].value as SystemStatusSubcategoryEnum]
                }
            })
        } else {
            navigate(getMonitoringIndexPage({ statuses: [subcategories[index].value] }))
        }
        trackEvent('button_click', 'monitoring', `Fleet Monitoring Summary Chart ${subcategories[index].value}`)
    }

    return <div className={`${className} relative h-fit`} {...props}>
        <DoughnutChart
            datasets={[{
                data: subcategories.map(item => summary.groups[item.value]) as number[],
                backgroundColor: secondaryDesign
                    ? ['#c5e6d6', '#ffb4a5', '#ffd8a2', '#fff37c']
                    : ['#ff7d6b', '#ffd8a2', '#fff37c', '#edebe7'],
                hoverBackgroundColor: secondaryDesign
                    ? ['#98d3b8', '#ff7d6b', '#ffb953', '#ffe200']
                    : ['#ff6347', '#ffc77d', '#ffe066', '#d1cec9'],
                borderColor: 'transparent',
                borderWidth: 0,
                ...type === 'donut'
                    ? {
                        cutout: 50,
                        radius: 70
                    }
                    : {
                        cutout: '70%',
                        circumference: 180,
                        rotation: -90
                    }
            }]}
            options={{
                aspectRatio: type === 'donut' ? 1 : 2,
                responsive: true,
                maintainAspectRatio: false
            }}
            customTooltip={index => <div className="font-body">
                <h4 className="flex text-xs whitespace-nowrap leading-[18px]">
                    <span className="font-semibold">{subcategories[index].title} </span>
                    {!!subcategories[index].severity && '-'} {subcategories[index].severity}
                </h4>
                <Button
                    design="btn-link"
                    className="whitespace-nowrap"
                    onClick={() => trackClickEvent(index)}
                >
                    {summary.groups[subcategories[index].value]?.format()} Sites
                </Button>
            </div>}
        />
        <div className={classnames('rounded-full flex justify-center items-center flex-col absolute left-1/2 -translate-x-1/2', type === 'donut' ? '-translate-y-1/2 top-1/2' : 'bottom-0')}>
            <h4
                className={classnames('font-display font-semibold', secondaryDesign ? 'text-2xl' : 'text-4xl')}
                data-test="monitoring-index-issues-count"
            >
                {summary.groups.monitored?.format()}
            </h4>
            <p className="text-gray-500 font-body text-xs text-center leading-[18px]" data-test="monitoring-index-issues-subtitle">
                {label}
            </p>
        </div>
    </div>
}

export default MonitoringIssuesChart
